import { ArrowRight } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/n4m96gyw_Martin%20Kucha%C5%99%20Workshop.png";

// Mapping: index of hashtag (translations.hero.hashtags[i]) → index of
// service filter to activate (translations.services.filters[i]).
// Filter indices:
//   0 = All Services
//   1 = Mentoring For Managers
//   2 = Agile Management Workshops
//   3 = Soft Skills Trainings
//   4 = OKR Certifications
//   5 = IT Consultations
const HASHTAG_TO_FILTER_INDEX = [
  1, // #Mentoring for managers     → Mentoring For Managers
  2, // #Agile                      → Agile Management Workshops
  2, // #Project Management         → Agile Management Workshops
  4, // #OKRs                       → OKR Certifications
  0, // #Trainings                  → All Services
  0, // #Workshops                  → All Services
  5, // #Consultations              → IT Consultations
];

export const Hero = () => {
  const { t } = useLanguage();

  const scrollToServices = () => {
    const el = document.getElementById("services");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Click on a hashtag selects the corresponding service filter and smoothly
  // scrolls to #services. The Services component listens for the custom
  // `set-service-filter` event and updates its own active filter.
  const handleHashtagClick = (filterIndex) => {
    window.dispatchEvent(
      new CustomEvent("set-service-filter", { detail: { filterIndex } }),
    );
    // Give the Services state a tick to update before scrolling.
    setTimeout(scrollToServices, 50);
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full min-h-[100svh] overflow-hidden bg-[#181B24]"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 hero-overlay" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-32 md:pt-40 lg:pt-44 pb-20 min-h-[100svh] flex flex-col justify-center">
        <div className="max-w-3xl">
          <h1
            data-testid="hero-headline"
            className="font-heading text-white font-bold uppercase leading-[1.02] tracking-[0.02em] text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem]"
          >
            {t.hero.headline.it}{" "}
            <span className="gradient-text">{t.hero.headline.change}</span>{" "}
            {t.hero.headline.with}{" "}
            <span className="gradient-text">{t.hero.headline.impact}</span>
          </h1>

          <p
            data-testid="hero-supporting-text"
            className="mt-8 text-white/85 text-base md:text-lg lg:text-xl leading-relaxed max-w-xl font-body"
          >
            {t.hero.paragraph}
          </p>

          <div className="mt-10 md:mt-12">
            <div
              data-testid="hero-domains-label"
              className="font-heading text-white text-lg md:text-xl tracking-[0.12em] uppercase mb-5"
            >
              {t.hero.domainsLabel}
            </div>

            <div className="hashtag-row max-w-2xl">
              {t.hero.hashtags.map((tag, i) => {
                const filterIndex = HASHTAG_TO_FILTER_INDEX[i] ?? 0;
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleHashtagClick(filterIndex)}
                    data-testid={`hashtag-${tag.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    className="rounded-full bg-[#181B24]/70 backdrop-blur-sm border border-white/15 text-white/95 px-7 py-3 text-base md:text-lg font-body font-medium whitespace-nowrap transition-all duration-200 cursor-pointer hover:bg-[#FFE21A] hover:text-[#181B24] hover:border-[#FFE21A] hover:-translate-y-0.5 hover:shadow-[0_10px_25px_rgba(255,226,26,0.3)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFE21A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#181B24]"
                    aria-label={`Filter služeb: ${tag}`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-10 md:mt-12">
            <button
              data-testid="hero-cta-button"
              onClick={scrollToServices}
              className="group inline-flex items-center gap-4 pl-2 pr-7 py-2 rounded-full gradient-bg text-[#181B24] font-heading font-bold tracking-[0.18em] uppercase text-sm md:text-base hover-lift shadow-[0_8px_30px_rgba(255,226,26,0.25)]"
            >
              <span className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white flex items-center justify-center group-hover:scale-105 group-hover:translate-x-0.5 transition-transform">
                <ArrowRight
                  size={22}
                  strokeWidth={2.75}
                  className="text-[#181B24]"
                />
              </span>
              {t.hero.cta}
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#F3F4F5] pointer-events-none" />
    </section>
  );
};

export default Hero;
